import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { setTeamHoleScore, writeTeamLocation, clearTeamLocation, setTeamMulligansUsed, setTeamDrivesUsed, reportMatchWinner } from '../lib/data.js'
import {
  computeGroupStatus,
  formatRemaining,
  getGroupId,
  PACE_LIMIT_MS,
  recordHoleSavedForGroup,
} from '../lib/paceOfPlay.js'
import CLUB from '../config/club.js'
import { ref, onValue } from 'firebase/database'
import { db } from '../lib/firebase.js'
import {
  findOpponent,
  fmtVsPar,
  getPlayStyle,
  headToHeadStatus,
  matchPlayResult,
  matchPlayStandings,
  nextHole,
  parsFor,
  prevHole,
  rankTeams,
  teamGross,
  teamThru,
  teamVsPar,
} from '../lib/scoring.js'
import { closestHoleByGreen } from '../config/courseHoles.js'
import { lastNames } from '../lib/teamName.js'
import useWakeLock from '../lib/useWakeLock.js'
import TeamCupScore from './TeamCupScore.jsx'

const PBCC_GREEN = CLUB.primaryColor
const PBCC_GOLD = CLUB.accentColor
const PBCC_GOLD_DARK = CLUB.accentColorDark || CLUB.accentColor

// ── Scorecard theme ───────────────────────────────────────────────────────
// Two themes share the same layout; only colors differ. DARK is the original
// dark-green + gold design. LIGHT is a white/light-gray, dark-green-text,
// green-button look for outdoor readability. Every scorecard sub-component
// reads the active token set from ScoreThemeCtx (useScoreTheme) so the toggle
// flips the whole screen at once. Default is LIGHT; choice persists to
// localStorage('scorecardTheme').
const SCORE_THEMES = {
  dark: {
    name: 'dark',
    pageBg: '#000000',
    text: '#FFFFFF',
    textDim: 'rgba(255,255,255,0.70)',
    textFaint: 'rgba(255,255,255,0.50)',
    accent: PBCC_GOLD,
    accentSoft: `${PBCC_GOLD}40`,
    headerBorder: 'rgba(255,255,255,0.12)',
    panel: '#111111',
    panelStrong: '#111111',
    panelBorder: 'rgba(255,255,255,0.10)',
    greenText: '#16A34A',
    thruBg: '#16A34A',
    thruText: '#FFFFFF',
    thruBorder: '#16A34A',
    holeNumber: '#FFFFFF',
    navArrowBg: '#16A34A',
    navArrowText: '#FFFFFF',
    boxBg: '#16A34A',
    boxBorder: '#16A34A',
    boxText: '#FFFFFF',
    selBg: '#FFFFFF',
    selText: '#123D26',
    entryBg: '#FFFFFF',
    entryText: '#123D26',
    entryBorder: 'rgba(0,0,0,0.15)',
    saveBg: '#16A34A',
    saveText: '#FFFFFF',
    ctaBg: PBCC_GOLD,
    ctaText: PBCC_GREEN,
    toggleBg: '#111111',
    toggleText: '#FFFFFF',
    toggleBorder: '#16A34A',
    rowBg: '#111111',
    rowBorder: 'rgba(255,255,255,0.10)',
    rowIconGreen: '#16A34A',
    rowChevron: '#16A34A',
    chipBg: 'rgba(255,255,255,0.10)',
    chipBorder: 'rgba(255,255,255,0.18)',
  },
  light: {
    name: 'light',
    pageBg: '#FFFFFF',
    text: PBCC_GREEN,
    textDim: 'rgba(18, 61, 38,0.70)',
    textFaint: 'rgba(18, 61, 38,0.50)',
    accent: PBCC_GREEN,
    accentSoft: 'rgba(18, 61, 38,0.14)',
    headerBorder: 'rgba(18, 61, 38,0.15)',
    panel: '#FFFFFF',
    panelStrong: '#FFFFFF',
    panelBorder: 'rgba(18, 61, 38,0.18)',
    greenText: PBCC_GREEN,
    thruBg: '#FFFFFF',
    thruText: PBCC_GREEN,
    thruBorder: 'rgba(18, 61, 38,0.18)',
    holeNumber: PBCC_GREEN,
    navArrowBg: PBCC_GREEN,
    navArrowText: '#FFFFFF',
    boxBg: 'transparent',
    boxBorder: PBCC_GREEN,
    boxText: PBCC_GREEN,
    selBg: PBCC_GREEN,
    selText: '#FFFFFF',
    entryBg: PBCC_GREEN,
    entryText: '#FFFFFF',
    entryBorder: 'rgba(18, 61, 38,0.30)',
    saveBg: PBCC_GREEN,
    saveText: '#FFFFFF',
    ctaBg: PBCC_GREEN,
    ctaText: '#FFFFFF',
    toggleBg: '#FFFFFF',
    toggleText: PBCC_GREEN,
    toggleBorder: PBCC_GREEN,
    rowBg: '#FFFFFF',
    rowBorder: 'rgba(18, 61, 38,0.12)',
    rowIconGreen: PBCC_GREEN,
    rowChevron: 'rgba(18, 61, 38,0.50)',
    chipBg: 'rgba(18, 61, 38,0.06)',
    chipBorder: 'rgba(18, 61, 38,0.15)',
  },
}

const ScoreThemeCtx = createContext(SCORE_THEMES.light)
const useScoreTheme = () => useContext(ScoreThemeCtx)

// Resolve the team's assigned starting hole from Firebase. Two sources,
// checked in order:
//
//   1. team.info.startingHole — written by the Tee Times tab's auto-assign
//      and by the per-team setTeamStartingHole API. Most direct.
//
//   2. Raw walk of tournaments/{id}/teeTimes/*/waves/*/assignments/{teamId}.
//      The Tee Times tab persists shotgun hole assignments here as
//      strings ("5", "5A", "12B") without always mirroring to team.info.
//      We DO NOT use lookupTeamTeeTimes for this — it filters out waves
//      whose flights map doesn't include this flightId, which silently
//      drops the assignment when admin assigned the team but never re-
//      saved the wave's flight list. We just need any non-empty value
//      tagged to this teamId, anywhere in the teeTimes tree.
//
// Returns 1 only when nothing is set anywhere.
function resolveStartingHole(tournament, flightId, teamId, team) {
  const fromInfo = Number(team?.info?.startingHole)
  if (Number.isFinite(fromInfo) && fromInfo >= 1 && fromInfo <= 18) return fromInfo

  const teeTimes = tournament?.teeTimes
  if (teeTimes && typeof teeTimes === 'object') {
    for (const dayKey of Object.keys(teeTimes)) {
      const day = teeTimes[dayKey]
      const waves = day && typeof day === 'object' ? day.waves : null
      if (!waves || typeof waves !== 'object') continue
      for (const waveKey of Object.keys(waves)) {
        const raw = waves[waveKey]?.assignments?.[teamId]
        if (raw == null || raw === '') continue
        // "5A" / "12B" → 5 / 12. Plain numbers and strings both work.
        const m = String(raw).match(/\d+/)
        if (!m) continue
        const n = Number(m[0])
        if (Number.isFinite(n) && n >= 1 && n <= 18) return n
      }
    }
  }
  return 1
}

// ── 15-MINUTE TEE GATE ───────────────────────────────────────────────────────
// The scorecard opens for everyone, but score ENTRY is locked until ~15 minutes
// before the group's tee time, then unlocks automatically for all four players.
// Mirrors the native app's teeGateFor(). No tee time on file → open (never blocks
// test rounds / walk-ups). Combine tournament date + the team's wave startTime.
const GATE_MINUTES = 15
function parseClock12(s) {
  const m = String(s || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
  if (!m) return null
  let h = Number(m[1]); const min = Number(m[2]); const ap = (m[3] || '').toUpperCase()
  if (ap === 'PM' && h < 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return { h, m: min }
}
function resolveTeeGate(tournament, teamId, nowMs) {
  const teeTimes = tournament?.teeTimes
  let startTime = null, dayIndex = 1
  if (teeTimes && typeof teeTimes === 'object') {
    for (const dayKey of Object.keys(teeTimes)) {
      const waves = teeTimes[dayKey]?.waves
      if (!waves || typeof waves !== 'object') continue
      let found = false
      for (const waveKey of Object.keys(waves)) {
        const a = waves[waveKey]?.assignments
        if (a && a[teamId] != null && a[teamId] !== '') {
          startTime = waves[waveKey]?.startTime || null
          const n = Number(dayKey); dayIndex = Number.isFinite(n) && n >= 1 ? n : 1
          found = true; break
        }
      }
      if (found) break
    }
  }
  const clock = parseClock12(startTime)
  const dm = String(tournament?.info?.date || '').match(/(\d{4})-(\d{2})-(\d{2})/)
  let teeAt = null
  if (clock && dm) {
    teeAt = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]) + (dayIndex - 1), clock.h, clock.m).getTime()
  }
  const open = teeAt == null || nowMs >= teeAt - GATE_MINUTES * 60_000
  return { startTime, teeAt, open }
}

// Which tournament day this team plays — found from the tee-time assignment
// tree (teeTimes/{day}/waves/*/assignments/{teamId}). Defaults to 1 for
// single-day events (and test tournaments with no tee times).
function resolveTeamDay(tournament, teamId) {
  const teeTimes = tournament?.teeTimes
  if (teeTimes && typeof teeTimes === 'object') {
    for (const dayKey of Object.keys(teeTimes)) {
      const day = teeTimes[dayKey]
      const waves = day && typeof day === 'object' ? day.waves : null
      if (!waves || typeof waves !== 'object') continue
      for (const waveKey of Object.keys(waves)) {
        const a = waves[waveKey]?.assignments
        if (a && a[teamId] != null && a[teamId] !== '') {
          const n = Number(dayKey)
          return Number.isFinite(n) && n >= 1 ? n : 1
        }
      }
    }
  }
  return 1
}

// ── MUTUAL MATCH RESULT ──────────────────────────────────────────────────────
// A winner is only SUBMITTED when BOTH teams agree. Two ways to agree:
//   • Hole-by-hole: both teams entered their scores and the head-to-head is
//     decided (inherently mutual — it's one result from both teams' scores).
//   • Enter winner only: BOTH teams entered a winner AND they match.
// One team's entry alone (or two entries that disagree) does NOT submit — the
// card just reads "waiting for both teams to agree." Nothing is flagged anywhere;
// players sort out any disagreement themselves. Both cards read the same shared
// data, so they always show the same state.
function officialMatchResult({ scoreWinnerId, myReportedWinnerId, oppReportedWinnerId, resultText }) {
  if (scoreWinnerId) return { status: 'submitted', winnerId: scoreWinnerId, fromScorecard: true, resultText: resultText || '' }
  if (myReportedWinnerId && oppReportedWinnerId && myReportedWinnerId === oppReportedWinnerId) {
    return { status: 'submitted', winnerId: myReportedWinnerId, fromScorecard: false }
  }
  if (myReportedWinnerId || oppReportedWinnerId) return { status: 'waiting' } // one entered, or they disagree
  return { status: 'none' }
}

export default function ScoreApp() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const tournamentId = params.get('t')
  const flightId = params.get('f')
  const teamId = params.get('team')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [hole, setHole] = useState(null) // initialized from team.info.startingHole
  const [showLeaderboard, setShowLeaderboard] = useState(false)
  const [online, setOnline] = useState(navigator.onLine)
  // When true, the Match Over / Round Complete takeover is dismissed so scores
  // can be corrected (wrong entry / accidental close-out) or a sudden-death
  // playoff hole can be scored. A banner on the scorecard flips it back.
  const [reopened, setReopened] = useState(false)

  // Light/dark scorecard theme. Defaults to LIGHT; remembers the player's
  // choice across QR scans via localStorage. T is the active token set passed
  // down through ScoreThemeCtx to every sub-component.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('scorecardTheme') === 'dark' ? 'dark' : 'light' }
    catch { return 'light' }
  })
  const T = SCORE_THEMES[theme] || SCORE_THEMES.light
  const toggleTheme = () => {
    setTheme((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark'
      try { localStorage.setItem('scorecardTheme', next) } catch {}
      return next
    })
  }

  // Pre-round welcome screen. Shown the first time a player opens their
  // scorecard (no scores yet) until they tap START ROUND. Persisted per
  // tournament+team so a refresh mid-round doesn't bounce them back to it.
  const [started, setStarted] = useState(() => {
    try { return localStorage.getItem(`scorecardStarted_${tournamentId}_${teamId}`) === '1' }
    catch { return false }
  })
  const startRound = () => {
    try { localStorage.setItem(`scorecardStarted_${tournamentId}_${teamId}`, '1') } catch {}
    setStarted(true)
  }

  // PLAYER-INITIATED rain delay — the player pauses THEIR OWN card and resumes it.
  // Scores already save to the server per hole, so nothing is lost; this is a
  // clear pause state, entirely independent of any admin rain delay. Persists per
  // device so a refresh keeps the card paused until the player taps Resume.
  const rainKey = `rainDelay_${tournamentId}_${teamId}`
  const [rainDelayed, setRainDelayed] = useState(() => {
    try { return localStorage.getItem(rainKey) === '1' } catch { return false }
  })
  const startRainDelay = () => { try { localStorage.setItem(rainKey, '1') } catch {}; setRainDelayed(true) }
  const resumeFromRain = () => { try { localStorage.removeItem(rainKey) } catch {}; setRainDelayed(false) }

  // Hold the screen awake the entire time the scorecard is mounted — the
  // screen must never dim on a player's scorecard, independent of the
  // top-level App wake lock. Wake Lock + silent-audio fallback live in a
  // shared hook so the broadcaster, scorecard, and /testphoto pages all
  // behave identically.
  useWakeLock(true)

  // Scorecard users arrive via a QR link with no trial code. Flag the session
  // so the trial gate lets them navigate freely — Home, Back, Course Map (and
  // its tap-to-dismiss back to the landing), Tournament Hub, etc. never bounce
  // them to the access-code screen. TrialGate reads this flag; the 'authchange'
  // event tells it to re-check without a reload. PinGate is untouched, so
  // /admin still requires a PIN.
  useEffect(() => {
    if (!tournamentId || !teamId) return
    try {
      if (sessionStorage.getItem('scorecardSession') !== 'true') {
        sessionStorage.setItem('scorecardSession', 'true')
        window.dispatchEvent(new Event('authchange'))
      }
    } catch {}
  }, [tournamentId, teamId])

  // GPS tracking — single watcher, throttled writes, persisted "on" flag
  // so subsequent visits auto-resume after the first user-gesture grant.
  // States: off | starting | on | denied | error | unsupported.
  const [gpsState, setGpsState] = useState(() => {
    try { return localStorage.getItem('gps_on') === '1' ? 'starting' : 'off' }
    catch { return 'off' }
  })
  const [gpsError, setGpsError] = useState('')
  const watchIdRef = useRef(null)
  const lastWriteAtRef = useRef(0)
  const ctxRef = useRef({ currentHole: 1, myName: '', flightId: flightId || '' })
  const gpsActive = gpsState === 'starting' || gpsState === 'on'

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament. Scan the QR code on your scorecard.')
      return
    }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  // QR scan / fresh page-load behavior: ALWAYS open on the team's assigned
  // starting hole (read below from team.info.startingHole or the tee-time
  // assignment). The lazy `hole ?? startingHole` fallback below is a render
  // safety net; this effect drives the actual `hole` STATE so anything
  // observing `hole` directly (not just `currentHole`) sees the correct
  // value as soon as Firebase team data resolves. Guarded by a ref so it
  // fires exactly once per mount — user nav (HoleNav buttons / save-and-
  // advance) takes over after that.
  const didInitHoleRef = useRef(false)
  useEffect(() => {
    if (didInitHoleRef.current) return
    if (!tournament) return
    const t = tournament.flights?.[flightId]?.teams?.[teamId]
    if (!t) return
    const sh = resolveStartingHole(tournament, flightId, teamId, t)
    didInitHoleRef.current = true
    setHole(sh)
  }, [tournament, flightId, teamId])

  // Hoisted scoring context — same derivation as below but with optional
  // chaining so the GPS watcher closure can read it before tournament/team
  // resolves. Refreshes ctxRef every render so writes always tag the
  // CURRENT hole, not the hole that was active when GPS was first enabled.
  const _team = tournament?.flights?.[flightId]?.teams?.[teamId]
  const _startingHole = resolveStartingHole(tournament, flightId, teamId, _team)
  const _currentHole = hole ?? _startingHole
  const _isPlayer = (tournament?.info?.format || '') === 'Stroke Play Individual'
  const _myName = _isPlayer
    ? (_team?.info?.players?.[0] || _team?.info?.name || '')
    : (_team?.info?.name || '')
  ctxRef.current = { currentHole: _currentHole, myName: _myName, flightId: flightId || '' }

  // Pace-of-play subscription. Every group's timer + hole position
  // lives at /paceOfPlay/{groupId}; we subscribe to the whole map so
  // the source-vs-delayed decision can compare this group against
  // every other group's elapsed time on each render.
  const [paceGroups, setPaceGroups] = useState({})
  useEffect(() => {
    if (!tournamentId) return
    const off = onValue(ref(db, `tournaments/${tournamentId}/paceOfPlay`), (snap) => {
      setPaceGroups(snap.val() || {})
    })
    return () => off()
  }, [tournamentId])

  // Local clock — re-renders once per second so the live countdown
  // refreshes even when no Firebase event arrives. Don't run unless we
  // have a group, otherwise we churn React state on a green screen.
  const [paceTick, setPaceTick] = useState(0)
  useEffect(() => {
    if (!tournamentId || !teamId) return
    const id = setInterval(() => setPaceTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [tournamentId, teamId])

  // Single GPS watcher tied to gpsActive. We do NOT include hole/teamName in
  // deps because changing them would tear down + restart watchPosition,
  // which on iOS pops the permission flow again. The watcher reads the
  // latest values via ctxRef on every fix.
  useEffect(() => {
    if (!gpsActive) return
    if (!tournamentId || !teamId) return
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGpsState('unsupported')
      return
    }
    // Defensive — clear any prior watcher before opening a new one so we
    // never stack multiple geolocation subscriptions on the same component.
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current) } catch {}
      watchIdRef.current = null
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsState('on')
        setGpsError('')
        // Accuracy gate — reject fixes worse than 20m before writing.
        // Stops occasional weak fixes (under-tree-cover, indoors at the
        // turn, etc.) from putting the dot in the wrong place on the
        // Command Center map. Browser-reported `accuracy` is a 68th-%ile
        // radius in meters; > 20m means GPS isn't tracking well enough
        // for hole-level positioning.
        if (!Number.isFinite(pos.coords.accuracy) || pos.coords.accuracy > 20) {
          return
        }
        // Throttle Firebase writes — geolocation may fire ~1Hz; one update
        // every 8s is plenty for live tracking on the Command Center map.
        const now = Date.now()
        if (now - lastWriteAtRef.current < 8000) return
        lastWriteAtRef.current = now
        const ctx = ctxRef.current
        // GPS-derived hole using the corrected green centers. Computed
        // every fix and written alongside the manual hole; Mark verifies
        // by comparing both values during on-course testing. Not used
        // to override the user's manual selection.
        const detected = closestHoleByGreen(pos.coords.latitude, pos.coords.longitude)
        writeTeamLocation(tournamentId, teamId, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          flightId: ctx.flightId,
          teamName: ctx.myName,
          hole: ctx.currentHole,
          detectedHole:        detected ? detected.number : null,
          detectedHoleMeters:  detected ? detected.meters : null,
        }).catch((e) => console.warn('[GPS] write failed', e))
      },
      (err) => {
        console.warn('[GPS] error', err.code, err.message)
        if (err.code === 1) {           // PERMISSION_DENIED
          setGpsState('denied')
          setGpsError('Location blocked. Enable in Settings → Safari → Location for this site.')
          try { localStorage.removeItem('gps_on') } catch {}
        } else if (err.code === 2) {    // POSITION_UNAVAILABLE
          setGpsState('error')
          setGpsError('GPS signal unavailable — move outdoors and try again.')
        } else if (err.code === 3) {
          // TIMEOUT — watchPosition keeps retrying internally; stay 'on'.
        } else {
          setGpsState('error')
          setGpsError(err.message || 'GPS error')
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    )

    return () => {
      if (watchIdRef.current != null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current) } catch {}
        watchIdRef.current = null
      }
    }
  }, [gpsActive, tournamentId, teamId])

  if (error) return <FatalError msg={error} />
  if (!tournament) return <Loading />

  // Team Cup scorekeeper takes over before flight/team resolution.
  // The `team` query param is interpreted as the Team Cup group CODE
  // rather than a flight team id.
  if (tournament.info?.format === 'Team Cup') {
    return (
      <TeamCupScore
        tournament={tournament}
        tournamentId={tournamentId}
        groupKey={teamId}
        pars={parsFor(tournament)}
      />
    )
  }

  const flight = tournament.flights?.[flightId]
  const team = flight?.teams?.[teamId]
  if (!flight || !team) return <FatalError msg="Team not found in this tournament." />

  const locked = !!team.info?.locked
  // 15-min tee gate — recomputed every render (paceTick ticks each second), so
  // the countdown updates live and it unlocks automatically at ~15 min pre-tee.
  const teeGate = resolveTeeGate(tournament, teamId, Date.now())
  const startingHole = resolveStartingHole(tournament, flightId, teamId, team)
  const pars = parsFor(tournament)
  const playStyle = getPlayStyle(tournament, flight)
  const opponent = findOpponent(flight, teamId)

  const format = tournament.info?.format || '4-Ball Best Ball'
  const eventType = tournament.info?.eventType || 'club'
  const isDemo = !!tournament.info?.isDemo
  const isStub = format === 'Stroke Play Big Team' || eventType === 'school'
  // Scramble / Stroke Play Individual: single-team score, no opponent, no
  // match status. The existing TeamHoleEntry handles the score input — we
  // just hide the opponent block + match pill.
  const isSoloFormat = format === 'Scramble' || format === 'Stroke Play Individual'
  const isMatchPlay = playStyle === 'match' && !isSoloFormat && !isStub

  // ── Match / sudden-death state ──────────────────────────────────────────
  // `decided` = someone won (clinch like 3&2, or ahead once all holes are in).
  // A TIE is not the end — in match play they keep playing extra holes until
  // someone wins, so sudden death keeps the scorecard live (no takeover) and
  // just advances onto playoff holes.
  const h2h = isMatchPlay && opponent ? headToHeadStatus(team, opponent) : null
  const matchResult = h2h ? matchPlayResult(h2h) : null
  const myThru = teamThru(team.scores)
  const oppThru = opponent ? teamThru(opponent.scores) : 0
  const decided = isMatchPlay && !!matchResult?.final
  const suddenDeath = isMatchPlay && !!matchResult?.suddenDeath && oppThru >= 18
  const regDone = myThru >= 18
  // OFFICIAL result — the score-derived winner combined with BOTH teams' reported
  // winners, so the SAME outcome shows on both cards (or a dispute is surfaced).
  const scoreWinnerId = (isMatchPlay && matchResult?.final) ? (matchResult.winner === 'me' ? teamId : opponent?.id) : null
  const official = isMatchPlay
    ? officialMatchResult({
        scoreWinnerId,
        myReportedWinnerId: team.info?.reportedWinnerId || null,
        oppReportedWinnerId: opponent?.info?.reportedWinnerId || null,
        resultText: matchResult?.resultText,
      })
    : { status: 'none' }
  // The active playoff hole: 19, advancing past every extra hole both teams
  // have already halved so both players stay on the same hole.
  let activePlayoffHole = 19
  if (suddenDeath) {
    while (activePlayoffHole < 36) {
      const my = team.scores?.[activePlayoffHole]?.bestBall
      const op = opponent?.scores?.[activePlayoffHole]?.bestBall
      if (Number.isFinite(my) && Number.isFinite(op) && my === op) { activePlayoffHole++; continue }
      break
    }
  }

  // In sudden death the scorecard sits on the active playoff hole; otherwise
  // it's the hole the player selected. Playoff holes (19+) are stored past the
  // regulation 18 (no overwrite) but show the REAL par of the hole being
  // replayed (19 → hole 1, 20 → hole 2, …) so they look like any other hole.
  const currentHole = suddenDeath ? activePlayoffHole : (hole ?? startingHole)
  const parForHole = (h) => (h > 18 ? pars[(h - 19) % 18] : pars[h - 1])
  const holePar = parForHole(currentHole)
  const score = team.scores?.[currentHole]
  const oppScore = opponent?.scores?.[currentHole]?.bestBall

  // Pace-of-play status for this scorecard's group. Re-derives on every
  // paceTick so the countdown ticks down in real time even while no new
  // Firebase event has arrived.
  const groupId = getGroupId(flightId, startingHole)
  const myGroup = paceGroups?.[groupId] || null
  void paceTick
  const paceLimits = {
    paceLimitMs: tournament.info?.paceLimitMs,
    paceWarnMs: tournament.info?.paceWarnMs,
  }
  const paceStatus = computeGroupStatus(myGroup, paceGroups, Date.now(), paceLimits)
  const isPlayer = format === 'Stroke Play Individual'
  const myLabel = isPlayer ? 'Your Score' : 'Your Team'
  const myName = isPlayer
    ? (team.info?.players?.[0] || lastNames(team.info?.name))
    : lastNames(team.info?.name)

  // Pre-round welcome screen: only before the round starts — no scores entered
  // yet AND the player hasn't tapped START ROUND. Once either is true, fall
  // through to the scorecard. (Team Cup returned earlier, so it's unaffected.)
  const hasAnyScore = !!(team.scores && Object.values(team.scores).some((s) => s != null && Number.isFinite(s.bestBall)))
  if (!hasAnyScore && !started) {
    return (
      <ScoreThemeCtx.Provider value={T}>
        <WelcomeScreen
          tournamentName={tournament.info?.name}
          day={resolveTeamDay(tournament, teamId)}
          teamName={myName || team.info?.name}
          startingHole={startingHole}
          onStart={startRound}
        />
      </ScoreThemeCtx.Provider>
    )
  }

  // Takeover ONLY when there's a result (or a solo round is done). Sudden death
  // keeps the scorecard live so play just continues, hole by hole. A reported
  // winner (or a dispute) on EITHER card also ends it — so both cards match.
  const roundOver = decided
    || (isMatchPlay && official.status !== 'none')
    || (isMatchPlay && regDone && !suddenDeath)
    || (!isMatchPlay && myThru === 18)

  if (roundOver && !reopened) {
    return (
      <ScoreThemeCtx.Provider value={T}>
        <MatchOverScreen
          tournamentName={tournament.info?.name}
          isMatchPlay={isMatchPlay}
          official={official}
          regDone={regDone}
          myTeamId={teamId}
          myName={myName}
          oppName={lastNames(opponent?.info?.name) || 'Opponent'}
          onEdit={() => setReopened(true)}
        />
      </ScoreThemeCtx.Provider>
    )
  }

  return (
    <ScoreThemeCtx.Provider value={T}>
    <div className="min-h-full relative" style={{ backgroundColor: T.pageBg }}>
      {/* GPS tracking pill. Single click handler (no double-bound onTouchEnd
          that previously stacked watchers on every tap). The actual watcher
          is owned by the useEffect tied to `gpsActive`; this button just
          toggles the state. iOS Safari needs the FIRST permission prompt
          to fire from a user gesture — setting state to 'starting' here
          satisfies that since the effect runs synchronously after commit. */}
      <GpsPill
        state={gpsState}
        error={gpsError}
        onToggle={() => {
          if (gpsActive) {
            setGpsState('off')
            setGpsError('')
            try { localStorage.removeItem('gps_on') } catch {}
            if (tournamentId && teamId) {
              clearTeamLocation(tournamentId, teamId).catch(() => {})
            }
          } else {
            setGpsState('starting')
            setGpsError('')
            try { localStorage.setItem('gps_on', '1') } catch {}
          }
        }}
      />

      <Header
        team={team}
        online={online}
        isDemo={isDemo}
        myDisplayName={myName}
      />

      <main className="max-w-md mx-auto p-3 space-y-2 pb-4">
        {/* When the takeover was dismissed to fix a score or play a sudden-death
            hole, this banner shows the match can be closed back out. */}
        {reopened && roundOver ? (
          <button
            type="button"
            onClick={() => setReopened(false)}
            className="w-full py-2 rounded-xl font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98]"
            style={{ backgroundColor: T.accent, color: '#123D26', border: '1px solid #123D26' }}>
            ← Back to match result
          </button>
        ) : null}
        {/* Theme toggle — big, obvious tap target near the top so non-tech
            users can flip light/dark without hunting. */}
        <button
          type="button"
          onClick={toggleTheme}
          className="w-full py-2 rounded-xl font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] flex items-center justify-center gap-2"
          style={{
            backgroundColor: T.toggleBg,
            color: T.toggleText,
            border: `2px solid ${T.toggleBorder}`,
          }}>
          {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </button>

        {isStub ? (
          <StubFormatNotice format={format} eventType={eventType} />
        ) : (
          <>
            {isMatchPlay ? (
              <MatchHero
                myTeam={{ id: teamId, ...team }}
                opponent={opponent}
              />
            ) : format === 'Scramble' ? (
              // Scrambles only need a "THRU N" indicator; the gross/vsPar
              // cards are unnecessary noise on a phone-sized scorecard.
              <div
                className="rounded-2xl text-center py-2.5 px-4"
                style={{
                  backgroundColor: T.thruBg,
                  border: `1px solid ${T.thruBorder}`,
                }}>
                <p className="text-base font-extrabold uppercase tracking-widest"
                   style={{ color: T.thruText }}>
                  THRU {teamThru(team.scores)}
                </p>
              </div>
            ) : (
              <ScoreSummary scores={team.scores} pars={pars} />
            )}

            <HoleNav
              hole={currentHole}
              onPrev={() => setHole(prevHole(currentHole))}
              onNext={() => setHole(nextHole(currentHole))}
              score={score}
              par={holePar}
              format={format} />

            {!isMatchPlay && !isSoloFormat ? (
              <MatchStatus
                tournament={tournament}
                flight={flight}
                myTeam={{ id: teamId, ...team }}
                opponent={opponent}
                pars={pars}
              />
            ) : null}

            <PaceBanner status={paceStatus} />

            <SectionLabel color={T.greenText}>{myLabel}</SectionLabel>
            {locked ? (
              <div
                className="rounded-2xl text-center py-6 px-4"
                style={{ backgroundColor: 'rgba(245,191,71,0.15)', border: `1px solid ${T.accent}` }}>
                <p className="font-bold" style={{ color: T.text }}>Scores locked by tournament admin.</p>
                <p className="text-sm mt-1" style={{ color: T.textDim }}>No more edits allowed.</p>
              </div>
            ) : !teeGate.open ? (
              <TeeGateCard gate={teeGate} T={T} />
            ) : rainDelayed ? (
              <RainDelayCard onResume={resumeFromRain} T={T} />
            ) : (
              <>
              <TeamHoleEntry
                tournamentId={tournamentId}
                flightId={flightId}
                teamId={teamId}
                hole={currentHole}
                par={holePar}
                score={score}
                teamName={myName}
                onSaved={() => {
                  // Pace only tracks regulation holes.
                  if (currentHole <= 18) {
                    recordHoleSavedForGroup({
                      tournamentId,
                      flightId,
                      startingHole,
                      completedHole: currentHole,
                      teamId,
                    }).catch(() => {})
                  }
                  // Advance to the next regulation hole. Once all 18 are in the
                  // takeover (result/awaiting) or sudden death takes over the
                  // display, so this wrap is never shown; in sudden death the
                  // current hole is pinned to the active playoff hole instead.
                  setHole((h) => nextHole(h ?? startingHole))
                }}
              />
              <button
                type="button"
                onClick={startRainDelay}
                className="w-full mt-1 py-2.5 rounded-xl font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98]"
                style={{ background: 'transparent', color: T.text, border: `1.5px solid ${T.accent}` }}>
                ⛈ Rain Delay — pause my card
              </button>
              </>
            )}

            {/* MATCH PLAY has no total score — offer "enter winner only" right on
                the scorecard, alongside the hole-by-hole entry. Shows once scoring
                is unlocked (after the group tees off). */}
            {isMatchPlay && opponent && teeGate.open && !rainDelayed ? (
              <EnterWinnerOnly
                tournamentId={tournamentId}
                flightId={flightId}
                teamId={teamId}
                oppId={opponent.id}
                myName={myName}
                oppName={lastNames(opponent?.info?.name) || 'Opponent'}
                T={T}
              />
            ) : null}

            {format === 'Scramble' ? (
              <ScrambleCountersCard
                tournamentId={tournamentId}
                flightId={flightId}
                teamId={teamId}
                team={team}
                tournamentInfo={tournament.info}
              />
            ) : null}

            {!isSoloFormat ? (
              opponent ? (
                <>
                  <SectionLabel color={T.accent}>Opponent</SectionLabel>
                  <OpponentLine
                    teamName={lastNames(opponent.info?.name)}
                    score={oppScore}
                    par={pars[currentHole - 1]}
                    opponentScores={opponent.scores}
                    myScores={team.scores}
                    currentHole={currentHole}
                    pars={pars}
                  />
                </>
              ) : (
                <div
                  className="rounded-2xl text-center py-3 px-4 text-sm uppercase tracking-widest"
                  style={{
                    color: T.textDim,
                    backgroundColor: T.panel,
                    border: `1px dashed ${T.panelBorder}`,
                  }}>
                  No opponent assigned · practice round
                </div>
              )
            ) : null}
          </>
        )}

        <ActionRow
          icon="🏆"
          iconBg={T.rowIconGreen}
          label={`${showLeaderboard ? 'Hide' : 'Show'} Flight Leaderboard`}
          onClick={() => setShowLeaderboard((s) => !s)}
        />

        {showLeaderboard ? <FlightMiniBoard flight={flight} myTeamId={teamId} pars={pars} playStyle={playStyle} /> : null}

        {/* Tournament Hub — leaderboard + next-day tee times + calcutta in one place. */}
        <ActionRow
          icon="📊"
          iconBg={PBCC_GOLD}
          label="View Full Tournament Hub"
          href={`/hub?t=${tournamentId}`}
        />

        {/* Photo submission — pre-fills team and current hole. */}
        <ActionRow
          icon="📸"
          iconBg="#E76F9E"
          label="Share a Photo"
          href={`/photos?t=${tournamentId}&team=${teamId}&hole=${currentHole || ''}`}
        />

        {/* Course Map — moved to the bottom (last tab). */}
        <ActionRow
          icon="🗺️"
          iconBg="#22B8CF"
          label="Course Map"
          onClick={() => navigate('/map')}
        />
      </main>
    </div>
    </ScoreThemeCtx.Provider>
  )
}

// Secondary action as a clean list row: colored circle icon on the left,
// label, right chevron. Theme-aware — light: white row, dark-green text;
// dark: dark row, white text. Same layout in both themes.
function ActionRow({ icon, iconBg, label, onClick, href }) {
  const T = useScoreTheme()
  const content = (
    <>
      <span style={{
        width: 36, height: 36, borderRadius: '50%', backgroundColor: iconBg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 17,
      }}>{icon}</span>
      <span className="flex-1 text-left font-bold text-sm uppercase tracking-wide" style={{ color: T.text }}>{label}</span>
      <span style={{ color: T.rowChevron, fontSize: 24, lineHeight: 1 }}>›</span>
    </>
  )
  const className = 'w-full flex items-center gap-3 py-3 px-4 rounded-xl transition active:scale-[0.99]'
  const style = {
    backgroundColor: T.rowBg,
    border: `1px solid ${T.rowBorder}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  }
  if (href) {
    return <a href={href} className={className} style={{ ...style, textDecoration: 'none' }}>{content}</a>
  }
  return <button type="button" onClick={onClick} className={className} style={style}>{content}</button>
}

// Pre-round welcome screen — the first thing a player sees after scanning the
// QR. The logo spins + zooms in (1.5s), then the tournament/day, team name,
// starting hole, and START ROUND button reveal in a staggered sequence. Pure
// CSS animations (keyframes injected via a <style> tag below). The text/button
// use animation-fill-mode "backwards" so they're hidden during their delay and
// settle to their natural visible state afterward (keeps the button's tap
// scale working). Theme-aware (light/dark) via useScoreTheme.
// Full-screen takeover shown when the match/round is over. Ends the scorecard
// so there's no looping back to hole 1 and no pace-of-play. For match play it
// names the winner + margin (e.g. "Ratliff/Keese wins 3 & 2"); for a side that
// finished 18 before the match is mathematically closed it says the round is in
// and awaits the opponent; for solo/stroke formats it just confirms the round.
function MatchOverScreen({ tournamentName, isMatchPlay, official, regDone, myTeamId, myName, oppName, onEdit }) {
  const T = useScoreTheme()

  const waiting = isMatchPlay && (official?.status === 'waiting' || (official?.status !== 'submitted' && regDone))

  let icon = '🏁'
  let headline = 'ROUND COMPLETE'
  let detail = 'All 18 holes are in. Nice round!'
  let footer = 'No more holes to play — thanks for playing.'

  if (isMatchPlay && official?.status === 'submitted') {
    // BOTH teams agree — the winner is submitted (from hole-by-hole or matching entries).
    const won = official.winnerId === myTeamId
    const winnerName = won ? myName : oppName
    const margin = official.fromScorecard ? String(official.resultText || '').replace('&', ' & ') : ''
    icon = won ? '🏆' : '🤝'
    headline = 'MATCH OVER'
    detail = margin ? `${winnerName} wins ${margin}` : `${winnerName} won the match`
    footer = 'Winner submitted — both teams agree.'
  } else if (waiting) {
    icon = '⏳'
    headline = 'WAITING FOR BOTH TEAMS'
    detail = 'Waiting for both teams to agree on the winner.'
    footer = 'It submits automatically once both teams enter the same winner.'
  }

  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6"
      style={{ backgroundColor: T.pageBg, minHeight: '100vh' }}>
      {tournamentName ? (
        <div className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: T.textDim }}>
          {tournamentName}
        </div>
      ) : null}
      <div style={{ fontSize: '3rem', lineHeight: 1 }}>{icon}</div>
      <div className="text-4xl font-extrabold uppercase mt-3 leading-tight" style={{ color: T.greenText }}>
        {headline}
      </div>
      <div className="text-lg font-bold mt-3 max-w-xs" style={{ color: T.textDim }}>
        {detail}
      </div>
      {footer ? (
        <div className="text-sm font-bold mt-2 max-w-xs" style={{ color: T.textDim }}>{footer}</div>
      ) : null}

      <div className="mt-8 w-full max-w-xs">
        <button
          type="button"
          onClick={onEdit}
          className="w-full py-2.5 rounded-2xl font-bold text-sm uppercase tracking-widest transition active:scale-[0.98]"
          style={{ backgroundColor: 'transparent', color: T.textDim, border: `1px solid ${T.textDim}` }}>
          ✎ {waiting ? 'Back to scorecard' : 'Edit scorecard'}
        </button>
      </div>

      <div className="text-sm mt-6 max-w-xs" style={{ color: T.textDim }}>
        {waiting ? 'Both teams enter the winner — when they match, it’s submitted.' : <>Wrong score? Tap <b>Edit scorecard</b> to fix it.</>}
      </div>
    </div>
  )
}

function WelcomeScreen({ tournamentName, day, teamName, startingHole, onStart }) {
  const T = useScoreTheme()
  // Load the Cinzel font from Google Fonts for the welcome screen only.
  // Injected once into <head>; no-op if the link is already present so it
  // never affects other screens or re-adds on remount.
  useEffect(() => {
    const id = 'cinzel-welcome-font'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;800&display=swap'
    document.head.appendChild(link)
  }, [])
  return (
    <div
      className="flex flex-col items-center justify-start text-center px-6 pt-10 pb-10"
      style={{ backgroundColor: T.pageBg, minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes wsLogoIn {
          0%   { opacity: 0; transform: scale(0) rotate(0deg); }
          100% { opacity: 1; transform: scale(1) rotate(1080deg); }
        }
        @keyframes wsFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wsBounceIn {
          0%   { opacity: 0; transform: scale(0.8); }
          60%  { opacity: 1; transform: scale(1.06); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <img
        src={T.pageBg === '#000000' ? CLUB.logoLight : CLUB.logo}
        alt=""
        style={{
          width: '300px', height: '300px', objectFit: 'contain', marginBottom: 12,
          animation: 'wsLogoIn 4.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both',
        }}
      />
      {tournamentName ? (
        <div
          className="text-lg font-bold uppercase tracking-wide"
          style={{ fontFamily: 'Cinzel, serif', color: T.textDim, animation: 'wsFadeUp 0.5s ease 4s backwards' }}>
          {tournamentName}
        </div>
      ) : null}
      <div
        className="text-4xl font-extrabold uppercase mt-1 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: T.greenText, animation: 'wsFadeUp 0.5s ease 4s backwards' }}>
        Welcome to Day {day}
      </div>
      <div
        className="text-4xl font-extrabold uppercase mt-5 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: T.text, animation: 'wsFadeUp 0.5s ease 4.5s backwards' }}>
        {teamName}
      </div>
      <div
        className="text-5xl font-extrabold uppercase mt-5 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: '#8B1A1A', animation: 'wsFadeUp 0.5s ease 5s backwards' }}>
        Teeing off on Hole #{startingHole}
      </div>
      <button
        type="button"
        onClick={onStart}
        className="mt-10 w-full max-w-xs py-4 rounded-2xl font-extrabold text-lg uppercase tracking-widest transition active:scale-[0.98]"
        style={{
          fontFamily: 'Cinzel, serif',
          background: T.saveBg,
          color: T.saveText,
          boxShadow: '0 6px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.25)',
          animation: 'wsBounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 5.5s backwards',
        }}>
        🏌️ Start Round →
      </button>
    </div>
  )
}

function GpsPill({ state, error, onToggle }) {
  const cfg = (() => {
    switch (state) {
      case 'on':
        return { label: '📍 GPS Live', sub: 'Sharing your position with the Command Center', accent: '#22C55E', cta: 'Tap to stop' }
      case 'starting':
        return { label: '📍 Locating…', sub: 'Acquiring GPS — keep this page open', accent: '#F59E0B', cta: '...' }
      case 'denied':
        return { label: '🚫 Location Blocked', sub: error || 'Enable in Settings → Safari → Location.', accent: '#EF4444', cta: 'Retry' }
      case 'error':
        return { label: '⚠️ GPS Error', sub: error || 'Try again outdoors with clear sky.', accent: '#EF4444', cta: 'Retry' }
      case 'unsupported':
        return { label: '🚫 GPS Not Available', sub: 'This browser does not support location.', accent: '#6B7280', cta: '' }
      case 'off':
      default:
        return { label: '📍 Enable GPS Tracking', sub: 'Tap to show your position on the course map', accent: PBCC_GOLD, cta: 'Tap' }
    }
  })()
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%',
        background: '#0F2A1B',
        color: '#FFF',
        padding: '10px 14px',
        border: 'none',
        borderTop: `2px solid ${cfg.accent}`,
        borderBottom: `2px solid ${cfg.accent}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: cfg.accent }}>{cfg.label}</div>
        {cfg.sub ? (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2, lineHeight: 1.3 }}>
            {cfg.sub}
          </div>
        ) : null}
      </div>
      {cfg.cta ? (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginLeft: 8, whiteSpace: 'nowrap' }}>
          {cfg.cta}
        </div>
      ) : null}
    </button>
  )
}

function Header({ team, online, isDemo, myDisplayName }) {
  const T = useScoreTheme()
  return (
    <header className="border-b" style={{ borderColor: T.headerBorder }}>
      <div className="max-w-md mx-auto p-3">
        {/* No Back/Home nav and no tournament/flight title — the header shows
            only the team name and the live indicator. Course Map and the other
            actions live in the list-row buttons at the bottom. */}
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-extrabold leading-tight truncate min-w-0" style={{ color: T.text }}>
            {myDisplayName || lastNames(team.info?.name)}
          </h2>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {isDemo ? (
              <span
                className="text-sm font-bold uppercase tracking-wider rounded-full px-2.5 py-1"
                style={{ backgroundColor: '#FFC663', color: '#123D26' }}>
                ⚠ Demo Mode
              </span>
            ) : null}
            <span
              className="text-sm font-bold uppercase tracking-wider rounded-full px-2.5 py-1"
              style={
                online
                  ? { backgroundColor: T.accentSoft, color: T.text }
                  : { backgroundColor: 'rgba(255,180,80,0.25)', color: '#B45309' }
              }>
              {online ? '● Live' : 'Queued'}
            </span>
          </div>
        </div>
      </div>
    </header>
  )
}

function ScoreSummary({ scores, pars }) {
  // Stroke-play summary cards. Match play uses MatchHero at the top of the
  // screen instead and skips this component entirely.
  const thru = teamThru(scores)
  const vp = teamVsPar(scores, pars)
  const gross = teamGross(scores)
  let vpColor = '#123D26'
  if (gross > 0 && vp < 0) vpColor = PBCC_GOLD
  if (gross > 0 && vp > 0) vpColor = '#FF6B6B'
  return (
    <div className="grid grid-cols-3 gap-3">
      <SummaryCard label="THRU" value={thru || '—'} />
      <SummaryCard label="GROSS" value={gross || '—'} />
      <SummaryCard label="VS PAR" value={fmtVsPar(vp)} valueColor={vpColor} />
    </div>
  )
}

function SummaryCard({ label, value, valueColor = '#123D26' }) {
  return (
    <div
      className="bg-white rounded-2xl py-4 px-2 text-center"
      style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }}>
      <div className="text-sm font-bold tracking-widest text-gray-500">{label}</div>
      <div className="text-3xl font-extrabold mt-0.5" style={{ color: valueColor }}>
        {value}
      </div>
    </div>
  )
}

function HoleNav({ hole, onPrev, onNext, score, par, format }) {
  const T = useScoreTheme()
  const isPlayoff = hole > 18
  return (
    <div
      className="rounded-2xl flex items-center justify-between px-3 py-2"
      style={{
        backgroundColor: T.panel,
        border: `1px solid ${T.panelBorder}`,
      }}>
      <button
        onClick={onPrev}
        className="text-4xl font-bold w-12 h-12 rounded-full flex items-center justify-center"
        style={{ color: T.navArrowText, backgroundColor: T.navArrowBg }}>
        ‹
      </button>
      <div className="text-center">
        <div className="text-2xl font-bold uppercase tracking-widest" style={{ color: T.greenText }}>
          {isPlayoff ? 'Playoff' : 'Hole'}
        </div>
        <div className="text-6xl font-extrabold leading-none mt-1" style={{ color: T.holeNumber }}>
          {isPlayoff ? hole - 18 : hole}
        </div>
        <div className="text-2xl font-bold mt-1" style={{ color: T.greenText }}>
          Par {par}
          {format === 'Scramble' ? null : (
            <>
              {' · '}Best ball:{' '}
              <span className="font-extrabold" style={{ color: T.text }}>{score?.bestBall ?? '—'}</span>
            </>
          )}
        </div>
      </div>
      <button
        onClick={onNext}
        className="text-4xl font-bold w-12 h-12 rounded-full flex items-center justify-center"
        style={{ color: T.navArrowText, backgroundColor: T.navArrowBg }}>
        ›
      </button>
    </div>
  )
}

// Tee-gate lock card — shown in place of the score entry until ~15 min before
// tee off. Counts down live (parent re-renders every second) and disappears the
// moment scoring unlocks, for all four players at once.
function TeeGateCard({ gate, T }) {
  const openAt = (gate.teeAt || 0) - GATE_MINUTES * 60_000
  const ms = Math.max(0, openAt - Date.now())
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  const sec = Math.floor((ms % 60000) / 1000)
  const countdown = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}:${String(sec).padStart(2, '0')}`
  return (
    <div className="rounded-2xl text-center py-7 px-4" style={{ backgroundColor: 'rgba(245,191,71,0.15)', border: `1px solid ${T.accent}` }}>
      <div style={{ fontSize: 34 }}>🔒</div>
      <p className="font-extrabold mt-1 text-lg" style={{ color: T.text }}>Scoring opens 15&nbsp;min before your tee time</p>
      {gate.startTime ? <p className="text-sm mt-1" style={{ color: T.textDim }}>Your tee time: <b style={{ color: T.text }}>{gate.startTime}</b></p> : null}
      <p className="text-3xl font-extrabold mt-3" style={{ color: T.text, fontVariantNumeric: 'tabular-nums' }}>Unlocks in {countdown}</p>
      <p className="text-sm mt-3" style={{ color: T.textDim }}>Unlocks automatically for all four players — no refresh needed.</p>
    </div>
  )
}

// Player-initiated rain delay — a clear paused state on THIS card. Scores are
// already saved on the server; Resume returns to scoring. No admin needed.
function RainDelayCard({ onResume, T }) {
  return (
    <div className="rounded-2xl text-center py-7 px-4" style={{ backgroundColor: 'rgba(59,130,246,0.12)', border: `1px solid ${T.accent}` }}>
      <div style={{ fontSize: 34 }}>⛈️</div>
      <p className="font-extrabold mt-1 text-lg" style={{ color: T.text }}>Rain Delay</p>
      <p className="text-sm mt-1" style={{ color: T.textDim }}>Your card is paused and saved — every score you entered is kept.</p>
      <button
        type="button"
        onClick={onResume}
        className="mt-5 w-full py-3.5 rounded-xl font-extrabold text-lg transition active:scale-[0.98]"
        style={{ background: PBCC_GREEN, color: '#FFFFFF', border: `1.5px solid ${T.accent}` }}>
        ▶ Resume scoring
      </button>
      <p className="text-sm mt-3" style={{ color: T.textDim }}>You control this — independent of any admin rain delay.</p>
    </div>
  )
}

// MATCH PLAY "enter winner only" — pick the winning team without a hole-by-hole
// card. Reports to the bracket (winnerId) or flags it for the pro shop.
function EnterWinnerOnly({ tournamentId, flightId, teamId, oppId, myName, oppName, T }) {
  const [mode, setMode] = useState('button') // button | pick | done
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const report = async (winnerId) => {
    setBusy(true); setErr('')
    const res = await reportMatchWinner(tournamentId, flightId, teamId, winnerId)
    setBusy(false)
    if (res === 'error') setErr('Could not report — check your signal and try again.')
    else setMode('done')
  }
  if (mode === 'done') {
    return (
      <div className="rounded-2xl text-center py-5 px-4" style={{ backgroundColor: 'rgba(31,122,68,0.14)', border: `1px solid ${T.accent}` }}>
        <div style={{ fontSize: 30 }}>⏳</div>
        <p className="font-extrabold" style={{ color: T.text }}>Winner entered</p>
        <p className="text-sm mt-1" style={{ color: T.textDim }}>It submits automatically once the other team enters the same winner.</p>
      </div>
    )
  }
  if (mode === 'pick') {
    const pickBtn = { width: '100%', marginTop: 10, padding: '14px', borderRadius: 14, fontWeight: 800, fontSize: 16, border: `1.5px solid ${T.accent}`, background: PBCC_GREEN, color: '#FFFFFF', cursor: 'pointer', opacity: busy ? 0.6 : 1 }
    return (
      <div className="rounded-2xl py-5 px-4" style={{ backgroundColor: T.pageBg, border: `1px solid ${T.accent}` }}>
        <p className="font-extrabold text-center text-lg" style={{ color: T.text }}>Who won the match?</p>
        <button disabled={busy} onClick={() => report(teamId)} style={pickBtn}>🏆 {myName} won</button>
        <button disabled={busy} onClick={() => report(oppId)} style={pickBtn}>🏆 {oppName} won</button>
        {err ? <p className="text-sm text-center mt-2" style={{ color: '#EF4444' }}>{err}</p> : null}
        <button onClick={() => setMode('button')} className="w-full mt-3 text-sm font-bold" style={{ color: T.textDim, background: 'transparent', border: 'none' }}>Cancel</button>
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={() => setMode('pick')}
      className="w-full py-3 rounded-xl font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98]"
      style={{ background: 'transparent', color: T.text, border: `1.5px solid ${T.accent}` }}>
      🏆 Enter winner only (skip the scorecard)
    </button>
  )
}

function TeamHoleEntry({ tournamentId, flightId, teamId, hole, par, score, teamName, onSaved }) {
  const T = useScoreTheme()
  // Default to par so the most common score is one tap away. A previously-saved
  // score wins (so navigating back to a hole shows what you actually shot).
  const [s, setS] = useState(score?.bestBall ?? par)

  useEffect(() => {
    setS(score?.bestBall ?? par)
  }, [hole, par, score?.bestBall])

  const save = async () => {
    const n = s === '' || s == null ? null : Number(s)
    await setTeamHoleScore(tournamentId, flightId, teamId, hole, n)
    onSaved?.()
  }

  const canSave = Number.isFinite(Number(s)) && Number(s) > 0

  return (
    <div className="space-y-2">
      <TeamScoreInput teamName={teamName} value={s} onChange={setS} par={par} />
      <button
        onClick={save}
        disabled={!canSave}
        className="w-full py-3 rounded-2xl font-extrabold text-lg uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-40"
        style={{
          background: T.saveBg,
          color: T.saveText,
          boxShadow: '0 6px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.25)',
        }}>
        ✓ Save & Next
      </button>
    </div>
  )
}

// Maps a hole score to its golf nickname emoji. Tracks the Golf Games
// Scorecard convention.
function scoreEmoji(score, par) {
  if (score === 1) return '🕳️'
  const diff = score - par
  if (diff <= -2) return '🦅🦅'
  if (diff === -1) return '🐦'
  if (diff === 0) return '⛳'
  if (diff === 1) return '😬'
  if (diff === 2) return '😱'
  if (diff === 3) return '💀'
  return '⛄'
}

function TeamScoreInput({ teamName, value, onChange, par }) {
  const T = useScoreTheme()
  const cur = value === '' ? null : Number(value)
  // Always render 1–8. Par-based clamping (lo = par − 2) hid the "1" and
  // "2" buttons on par-4 / par-5 holes, which played as "the selector
  // won't go below my current score" — the actual user-facing bug.
  const lo = 1
  const hi = 8
  const buttons = []
  for (let n = lo; n <= hi; n++) buttons.push(n)
  const emoji = cur != null ? scoreEmoji(cur, par) : null
  return (
    <div
      className="rounded-2xl p-3"
      style={{
        backgroundColor: T.panel,
        border: `1px solid ${T.panelBorder}`,
      }}>
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-base" style={{ color: T.text }}>{teamName}</span>
        <div className="flex items-center gap-2">
          {emoji ? <span className="text-3xl leading-none" aria-hidden>{emoji}</span> : null}
          <span
            className="text-5xl font-extrabold w-14 h-14 rounded-xl flex items-center justify-center"
            style={{
              backgroundColor: T.entryBg,
              color: T.entryText,
              border: `1.5px solid ${T.entryBorder}`,
            }}>
            {cur ?? '—'}
          </span>
        </div>
      </div>
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${buttons.length}, minmax(0, 1fr))` }}>
        {buttons.map((n) => {
          const selected = cur === n
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(String(n))}
              className="py-2.5 rounded-xl text-base font-extrabold transition active:scale-[0.96]"
              style={
                selected
                  ? {
                      backgroundColor: T.selBg,
                      color: T.selText,
                      boxShadow: '0 4px 12px rgba(18, 61, 38,0.45)',
                    }
                  : {
                      backgroundColor: T.boxBg,
                      color: T.boxText,
                      border: `1.5px solid ${T.boxBorder}`,
                    }
              }>
              {n}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Hero match-status card shown at the top of the score screen for match-play
// tournaments. Big gold panel with the leader name + UP count; small subtitle
// with thru/down detail. Replaces the THRU summary card entirely.
function MatchHero({ myTeam, opponent }) {
  if (!opponent) {
    return (
      <HeroCard
        title="PRACTICE ROUND"
        subtitle="No opponent assigned"
      />
    )
  }
  const { myWins, oppWins, played } = headToHeadStatus(myTeam, opponent)
  const result = matchPlayResult({ myWins, oppWins, played })
  const myName = lastNames(myTeam?.info?.name) || 'Your team'
  const oppName = lastNames(opponent?.info?.name) || 'Opponent'

  // No "NOT STARTED" banner — players go straight to the hole scoring view.
  if (played === 0) return null

  // Match decided — clinch (e.g. 3UP with 2 to play) or 18 holes complete.
  if (result.final) {
    if (result.winner === 'tie') {
      return <HeroCard title="FINAL — HALVED" subtitle="all square thru 18" />
    }
    const winnerName = result.winner === 'me' ? myName : oppName
    return <HeroCard title={`FINAL — ${winnerName} WINS ${result.resultText}`} subtitle="match closed" />
  }

  if (myWins === oppWins) {
    return <HeroCard title="ALL SQUARE" subtitle={`thru ${played}`} />
  }
  if (myWins > oppWins) {
    const gap = myWins - oppWins
    return (
      <HeroCard
        title={`${myName} — ${gap} UP`}
        subtitle={`thru ${played}`}
      />
    )
  }
  const gap = oppWins - myWins
  return (
    <HeroCard
      title={`${oppName} — ${gap} UP`}
      subtitle={`you ${gap} DOWN · thru ${played}`}
    />
  )
}

function HeroCard({ title, subtitle }) {
  return (
    <div
      className="rounded-2xl px-5 py-5 text-center"
      style={{
        backgroundColor: PBCC_GOLD,
        boxShadow: '0 8px 24px rgba(18, 61, 38,0.4), inset 0 1px 0 rgba(255,255,255,0.25)',
      }}>
      <p
        className="font-extrabold uppercase leading-tight"
        style={{
          color: PBCC_GREEN,
          fontSize: 'clamp(1.5rem, 6.5vw, 2rem)',
          letterSpacing: '0.02em',
        }}>
        {title}
      </p>
      {subtitle ? (
        <p
          className="text-sm font-bold uppercase tracking-widest mt-1"
          style={{ color: 'rgba(18, 61, 38,0.75)' }}>
          {subtitle}
        </p>
      ) : null}
    </div>
  )
}

function MatchStatus({ tournament, flight, myTeam, opponent, pars }) {
  const playStyle = getPlayStyle(tournament, flight)
  // Head-to-head only when match play AND a paired opponent exists.
  if (playStyle === 'match' && opponent) {
    return <HeadToHeadStatus myTeam={myTeam} opponent={opponent} />
  }
  const teams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
  if (teams.length === 0) return null
  return playStyle === 'match'
    ? <MatchPlayStatus teams={teams} myTeamId={myTeam.id} />
    : <StrokePlayStatus teams={teams} myTeamId={myTeam.id} pars={pars} />
}

function HeadToHeadStatus({ myTeam, opponent }) {
  const { myWins, oppWins, played } = headToHeadStatus(myTeam, opponent)
  const result = matchPlayResult({ myWins, oppWins, played })
  const myName = lastNames(myTeam?.info?.name) || 'Your team'
  const oppName = lastNames(opponent?.info?.name) || 'Opponent'
  const thru = played > 0 ? ` · thru ${played}` : ''

  if (played === 0) {
    return <StatusPill label={`${myName} vs ${oppName} · Not started`} />
  }
  if (result.final) {
    if (result.winner === 'tie') {
      return <StatusPill highlight label={`FINAL · Halved (AS thru 18)`} />
    }
    const winnerName = result.winner === 'me' ? myName : oppName
    return <StatusPill highlight label={`FINAL · ${winnerName} wins ${result.resultText}`} />
  }
  if (myWins === oppWins) {
    return <StatusPill label={`All Square${thru}`} />
  }
  if (myWins > oppWins) {
    const gap = myWins - oppWins
    return <StatusPill highlight label={`${myName} — ${gap} UP${thru}`} />
  }
  const gap = oppWins - myWins
  return <StatusPill label={`${oppName} — ${gap} UP · you ${gap} DOWN${thru}`} />
}

function SectionLabel({ children, color = PBCC_GOLD }) {
  return (
    <p
      className="text-sm font-extrabold uppercase tracking-widest mt-2 mb-[-4px]"
      style={{ color }}>
      {children}
    </p>
  )
}

function OpponentLine({ teamName, score, par, opponentScores, myScores, currentHole, pars }) {
  const T = useScoreTheme()
  // Live opponent THRU and per-hole match status. Computed inline from the
  // shared tournament listener so they update in real time without any
  // extra Firebase subscription. Keeping these on the same panel makes it
  // obvious the opponent's writes are reaching the device.
  const oppThru = teamThru(opponentScores)
  let myWins = 0, oppWins = 0, played = 0
  for (let h = 1; h <= 18; h++) {
    const my = myScores?.[h]?.bestBall
    const op = opponentScores?.[h]?.bestBall
    if (!Number.isFinite(my) || !Number.isFinite(op)) continue
    played++
    if (my < op) myWins++
    else if (op < my) oppWins++
  }
  const gap = myWins - oppWins
  let matchStatus
  if (played === 0) matchStatus = 'Not started'
  else if (gap === 0) matchStatus = `ALL SQUARE THRU ${played}`
  else if (gap > 0) matchStatus = `You ${gap} UP THRU ${played}`
  else matchStatus = `Opp ${-gap} UP THRU ${played}`

  // Opponent's most-recently-submitted hole. Walk backwards from 18 to find
  // the highest hole index they have a best-ball for. This is what stays on
  // screen permanently — the user wants to always see what the opponent
  // posted on their last completed hole, not just the current hole's number.
  let lastHole = null
  let lastScore = null
  for (let h = 18; h >= 1; h--) {
    const s = opponentScores?.[h]?.bestBall
    if (Number.isFinite(s)) { lastHole = h; lastScore = s; break }
  }
  const lastPar = lastHole != null && pars ? pars[lastHole - 1] : null
  const lastEmoji = lastScore != null && lastPar != null ? scoreEmoji(lastScore, lastPar) : null

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        backgroundColor: T.panel,
        border: `1px solid ${T.panelBorder}`,
      }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-base truncate" style={{ color: T.text }}>{teamName}</div>
          <div className="text-sm mt-0.5 font-bold" style={{ color: T.text }}>
            ● Live · THRU {oppThru}
          </div>
        </div>
        <div
          className="rounded-full text-center py-1.5 px-3 text-sm font-extrabold uppercase tracking-wider whitespace-nowrap"
          style={{
            backgroundColor: T.chipBg,
            color: T.text,
            border: `1px solid ${T.chipBorder}`,
          }}>
          {matchStatus}
        </div>
      </div>

      {/* Permanent "last hole" panel — stays on screen no matter which hole
          the player is currently viewing. Updates when the opponent submits
          a new score (next render after the Firebase snapshot arrives). */}
      <div
        className="mt-3 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
        style={{
          backgroundColor: T.chipBg,
          border: `1px solid ${T.panelBorder}`,
        }}>
        <div className="min-w-0">
          <div
            className="text-sm uppercase tracking-widest font-extrabold"
            style={{ color: T.text }}>
            Opponent's Last Hole
          </div>
          {lastHole != null ? (
            <div className="text-base font-bold mt-0.5" style={{ color: T.text }}>
              Hole {lastHole} · Par {lastPar ?? '?'}
            </div>
          ) : (
            <div className="text-sm font-semibold italic mt-0.5" style={{ color: T.textDim }}>
              Opponent has not posted a hole yet
            </div>
          )}
        </div>
        {lastHole != null ? (
          <div className="flex items-center gap-2 shrink-0">
            {lastEmoji ? <span className="text-2xl leading-none" aria-hidden>{lastEmoji}</span> : null}
            <span
              className="text-3xl font-extrabold w-14 h-14 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: T.entryBg,
                color: T.entryText,
                border: `1.5px solid ${T.panelBorder}`,
              }}>
              {lastScore}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function StatusPill({ label, highlight }) {
  const T = useScoreTheme()
  return (
    <div
      className="rounded-2xl text-center py-3 px-4"
      style={{
        backgroundColor: highlight ? T.accentSoft : T.panel,
        border: `1px solid ${highlight ? T.accent : T.panelBorder}`,
      }}>
      <p
        className="text-sm font-bold uppercase tracking-widest"
        style={{ color: highlight ? T.accent : T.text }}>
        {label}
      </p>
    </div>
  )
}

function pluralStrokes(n) {
  return n === 1 ? '1 stroke' : `${n} strokes`
}

function StrokePlayStatus({ teams, myTeamId, pars }) {
  const ranked = rankTeams(teams, false, pars)
  // Skip teams that haven't started; if no one has, fall back to all teams.
  const started = ranked.filter((t) => t.thru > 0)
  const pool = started.length > 0 ? started : ranked
  const best = pool[0].gross
  const tiedForLead = pool.filter((t) => t.gross === best)
  const me = ranked.find((t) => t.id === myTeamId)

  if (tiedForLead.length > 1) {
    const meTied = tiedForLead.some((t) => t.id === myTeamId)
    return (
      <StatusPill
        highlight={meTied}
        label={meTied ? 'All Square at the top' : `${tiedForLead.length}-way tie at ${best}`}
      />
    )
  }

  const leader = tiedForLead[0]
  const second = pool[1]
  const gap = second ? second.gross - leader.gross : 0

  if (leader.id === myTeamId) {
    return (
      <StatusPill
        highlight
        label={
          gap > 0
            ? `${lastNames(leader.info?.name)} leads by ${pluralStrokes(gap)}`
            : `${lastNames(leader.info?.name)} leads`
        }
      />
    )
  }

  const myGap = (me?.gross ?? 0) - leader.gross
  return (
    <StatusPill
      label={`${lastNames(leader.info?.name)} leads by ${pluralStrokes(gap)} · you ${myGap > 0 ? `${pluralStrokes(myGap)} back` : 'tied'}`}
    />
  )
}

function MatchPlayStatus({ teams, myTeamId }) {
  const ranked = matchPlayStandings(teams)
  const totalPlayed = ranked.reduce((n, t) => n + t.holesWon, 0)
  if (totalPlayed === 0) {
    return <StatusPill label="All Square — match not started" />
  }
  const top = ranked[0].holesWon
  const tiedForLead = ranked.filter((t) => t.holesWon === top)
  const me = ranked.find((t) => t.id === myTeamId)

  if (tiedForLead.length > 1) {
    const meTied = tiedForLead.some((t) => t.id === myTeamId)
    return (
      <StatusPill
        highlight={meTied}
        label={
          top === 0
            ? 'All Square'
            : meTied
              ? `All Square — ${top} holes each`
              : `${tiedForLead.length}-way tie at ${top} holes`
        }
      />
    )
  }

  const leader = tiedForLead[0]
  const second = ranked[1]
  const gap = second ? leader.holesWon - second.holesWon : leader.holesWon

  if (leader.id === myTeamId) {
    return <StatusPill highlight label={`${lastNames(leader.info?.name)} — ${gap} UP`} />
  }

  const myGap = leader.holesWon - (me?.holesWon ?? 0)
  return (
    <StatusPill
      label={`${lastNames(leader.info?.name)} — ${gap} UP · you ${myGap} DOWN`}
    />
  )
}

function FlightMiniBoard({ flight, myTeamId, pars, playStyle }) {
  const T = useScoreTheme()
  // Match-play tournaments get the per-pair match-status board (with FINAL
  // rollup when a match clinches); stroke gets the existing Thru / vs Par
  // table. Branch on playStyle so the match-play tournament's player popup
  // matches what /leaderboard and /hub already show.
  if (playStyle === 'match') {
    return <FlightMiniBoardMatch flight={flight} myTeamId={myTeamId} />
  }
  const teams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
  const ranked = rankTeams(teams, false, pars)
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        backgroundColor: T.panel,
        border: `1px solid ${T.panelBorder}`,
      }}>
      <h3 className="font-bold text-sm uppercase tracking-widest mb-3" style={{ color: T.text }}>
        {flight.info?.name} · Live
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-sm uppercase tracking-widest border-b" style={{ borderColor: T.panelBorder, color: T.textDim }}>
            <th className="text-left py-2 w-8">#</th>
            <th className="text-left py-2">Team</th>
            <th className="text-right py-2">Thru</th>
            <th className="text-right py-2">vs Par</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((r, i) => {
            const isMe = r.id === myTeamId
            return (
              <tr
                key={r.id}
                className="border-b last:border-0"
                style={{
                  borderColor: T.panelBorder,
                  backgroundColor: isMe ? T.accentSoft : 'transparent',
                }}>
                <td className="py-2 font-bold" style={{ color: isMe ? T.accent : T.textDim }}>{i + 1}</td>
                <td className="py-2 font-semibold" style={{ color: T.text }}>{lastNames(r.info?.name)}</td>
                <td className="py-2 text-right" style={{ color: T.textDim }}>{r.thru}</td>
                <td className="py-2 text-right font-bold" style={{ color: isMe ? T.accent : T.text }}>{fmtVsPar(r.vsPar)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Match-play variant of the player's leaderboard popup. Mirrors the layout
// from /leaderboard's MatchFlightBoard: Team A | Status | Team B, with FINAL
// banner when a match has clinched. My team's row is highlighted gold.
function FlightMiniBoardMatch({ flight, myTeamId }) {
  const T = useScoreTheme()
  const teams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
  const byId = Object.fromEntries(teams.map((t) => [t.id, t]))
  const seen = new Set()
  const matches = []
  for (const t of teams) {
    if (seen.has(t.id)) continue
    const oppId = t.info?.opponentId
    if (oppId && byId[oppId] && oppId !== t.id) {
      seen.add(t.id); seen.add(oppId)
      const opp = byId[oppId]
      const h2h = headToHeadStatus(t, opp)
      const gap = h2h.myWins - h2h.oppWins
      let teamA, teamB, leaderGap, aWins, bWins
      if (gap >= 0) { teamA = t; teamB = opp; leaderGap = gap; aWins = h2h.myWins; bWins = h2h.oppWins }
      else          { teamA = opp; teamB = t; leaderGap = -gap; aWins = h2h.oppWins; bWins = h2h.myWins }
      const result = matchPlayResult({ myWins: aWins, oppWins: bWins, played: h2h.played })
      matches.push({ key: `${t.id}-${opp.id}`, teamA, teamB, leaderGap, played: h2h.played, result })
    } else {
      seen.add(t.id)
      matches.push({ key: `solo-${t.id}`, teamA: t, teamB: null, leaderGap: 0, played: 0, result: null })
    }
  }
  matches.sort((a, b) => {
    const af = a.result?.final ? 1 : 0
    const bf = b.result?.final ? 1 : 0
    if (af !== bf) return bf - af
    if (b.leaderGap !== a.leaderGap) return b.leaderGap - a.leaderGap
    return b.played - a.played
  })

  return (
    <div
      className="rounded-2xl p-4"
      style={{ backgroundColor: T.panel, border: `1px solid ${T.panelBorder}` }}>
      <h3 className="font-bold text-sm uppercase tracking-widest mb-3" style={{ color: T.text }}>
        {flight.info?.name} · Live
      </h3>
      <ul className="space-y-2">
        {matches.map((m) => {
          if (!m.teamB) {
            return (
              <li key={m.key} className="text-sm" style={{ color: T.textDim }}>
                {lastNames(m.teamA.info?.name)} — no opponent
              </li>
            )
          }
          const mineOnTeamA = m.teamA.id === myTeamId
          const mineOnTeamB = m.teamB.id === myTeamId
          const isMine = mineOnTeamA || mineOnTeamB
          const isFinal = !!m.result?.final
          const halved = isFinal && m.result?.winner === 'tie'
          let statusLine
          if (isFinal) {
            statusLine = halved
              ? 'FINAL · Halved'
              : `FINAL · ${lastNames(m.teamA.info?.name)} wins ${m.result.resultText}`
          } else if (m.played === 0) {
            statusLine = 'Not started'
          } else if (m.leaderGap === 0) {
            statusLine = `ALL SQUARE THRU ${m.played}`
          } else {
            statusLine = `${lastNames(m.teamA.info?.name)} ${m.leaderGap} UP THRU ${m.played}`
          }
          return (
            <li
              key={m.key}
              className="rounded-xl px-3 py-2"
              style={{
                backgroundColor: isMine ? T.accentSoft : 'transparent',
                border: `1px solid ${isMine ? T.accent : T.panelBorder}`,
              }}>
              <div className="text-sm font-bold truncate" style={{ color: T.text }}>
                {lastNames(m.teamA.info?.name)}{mineOnTeamA ? ' (you)' : ''}
                <span style={{ color: T.textFaint }}> vs </span>
                {lastNames(m.teamB.info?.name)}{mineOnTeamB ? ' (you)' : ''}
              </div>
              <div
                className="text-sm font-extrabold uppercase tracking-wider mt-1"
                style={{
                  color: isFinal
                    ? T.accent
                    : (m.leaderGap === 0 ? T.accent : T.text),
                }}>
                {statusLine}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// Scramble per-team counters: mulligans-remaining + per-player minimum-drive
// tracker. Only renders the rows that are enabled in tournament.info.
// Writes go through Firebase via the lib/data.js helpers — admin sees the
// same counters update live in their grid.
function ScrambleCountersCard({ tournamentId, flightId, teamId, team, tournamentInfo }) {
  const T = useScoreTheme()
  const mulligansAllowed = Number(tournamentInfo?.scrambleMulligans) || 0
  const minDriveEnabled = !!tournamentInfo?.scrambleMinDriveEnabled
  const minDrivesPerPlayer = Number(tournamentInfo?.scrambleMinDrivesPerPlayer) || 0
  const players = team.info?.players || []
  if (mulligansAllowed === 0 && !minDriveEnabled) return null

  const mulligansUsed = Number(team.info?.mulligansUsed) || 0
  const mulligansLeft = Math.max(0, mulligansAllowed - mulligansUsed)
  const drivesUsedRaw = team.info?.drivesUsed || {}
  const drivesUsed = players.map((_, i) => Number(drivesUsedRaw[i]) || 0)

  const useMulligan = () => {
    if (mulligansLeft <= 0) return
    setTeamMulligansUsed(tournamentId, flightId, teamId, mulligansUsed + 1)
      .catch((e) => console.warn('[scramble] mulligan write failed', e))
  }
  const undoMulligan = () => {
    if (mulligansUsed <= 0) return
    setTeamMulligansUsed(tournamentId, flightId, teamId, mulligansUsed - 1)
      .catch((e) => console.warn('[scramble] mulligan undo failed', e))
  }
  const adjustDrive = (idx, delta) => {
    const next = drivesUsed.slice()
    next[idx] = Math.max(0, (next[idx] || 0) + delta)
    setTeamDrivesUsed(tournamentId, flightId, teamId, next)
      .catch((e) => console.warn('[scramble] drive write failed', e))
  }

  return (
    <div
      className="rounded-2xl p-4 space-y-3"
      style={{ backgroundColor: T.panel, border: `1px solid ${T.panelBorder}` }}>
      <div className="text-sm uppercase tracking-widest font-extrabold" style={{ color: T.text }}>
        Scramble
      </div>

      {mulligansAllowed > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-extrabold" style={{ color: T.text }}>🎯 Mulligans</div>
            <div className="text-sm" style={{ color: T.textDim }}>{mulligansLeft} of {mulligansAllowed} remaining</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={undoMulligan}
              disabled={mulligansUsed <= 0}
              className="rounded-lg w-9 h-9 font-extrabold text-base disabled:opacity-30"
              style={{ backgroundColor: T.accentSoft, color: T.text, border: `1px solid ${T.panelBorder}` }}
              aria-label="Undo last mulligan">−</button>
            <button
              type="button"
              onClick={useMulligan}
              disabled={mulligansLeft <= 0}
              className="rounded-lg px-4 h-9 font-extrabold text-sm uppercase tracking-wider disabled:opacity-30"
              style={{ backgroundColor: T.ctaBg, color: T.ctaText }}>
              Use 1
            </button>
          </div>
        </div>
      ) : null}

      {minDriveEnabled ? (
        <div>
          <div className="text-sm font-extrabold mb-1" style={{ color: T.text }}>⛳ Drives Used (min {minDrivesPerPlayer} each)</div>
          {players.length === 0 ? (
            <div className="text-sm italic" style={{ color: T.textDim }}>No players on this team yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {players.map((p, i) => {
                const used = drivesUsed[i] || 0
                const short = used < minDrivesPerPlayer
                return (
                  <li key={i} className="flex items-center justify-between gap-2 rounded-lg px-3 py-1.5"
                      style={{ backgroundColor: short ? 'rgba(239,68,68,0.08)' : 'transparent', border: `1px solid ${short ? '#EF4444' : T.panelBorder}` }}>
                    <span className="text-sm font-bold truncate" style={{ color: T.text }}>{p}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => adjustDrive(i, -1)}
                        disabled={used <= 0}
                        className="rounded-md w-7 h-7 font-extrabold disabled:opacity-30"
                        style={{ backgroundColor: T.accentSoft, color: T.text }}
                        aria-label={`Remove drive for ${p}`}>−</button>
                      <span className="text-base font-extrabold w-8 text-center"
                            style={{ color: short ? '#DC2626' : T.accent }}>{used}</span>
                      <button
                        type="button"
                        onClick={() => adjustDrive(i, 1)}
                        className="rounded-md w-7 h-7 font-extrabold"
                        style={{ backgroundColor: T.ctaBg, color: T.ctaText }}
                        aria-label={`Add drive for ${p}`}>+</button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
          {drivesUsed.some((u) => u < minDrivesPerPlayer) ? (
            <div className="text-sm mt-1.5 font-bold" style={{ color: '#DC2626' }}>
              ⚠ One or more players below the minimum-drive threshold.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function StubFormatNotice({ format, eventType }) {
  const T = useScoreTheme()
  const isSchool = eventType === 'school'
  const heading = isSchool ? 'High School / College Event' : 'Stroke Play Big Team'
  const closest = isSchool
    ? 'Stroke Play Individual'
    : '4-Ball Stroke Play or Stroke Play Individual'
  const detail = isSchool
    ? 'Schools competing with best-N-of-M aggregation and a two-section leaderboard. The data model and live aggregation logic are in development.'
    : 'Teams of 3–5 with team total = sum of best N individual scores. The per-player data model and aggregator are in development.'
  return (
    <div
      className="rounded-2xl px-5 py-6 text-center"
      style={{
        backgroundColor: T.panel,
        border: `1.5px dashed ${T.accent}`,
      }}>
      <div className="text-3xl mb-2">🚧</div>
      <p className="font-extrabold uppercase tracking-widest text-base" style={{ color: T.text }}>
        {heading} · In Development
      </p>
      <p className="text-sm mt-3 leading-relaxed" style={{ color: T.textDim }}>{detail}</p>
      <p className="text-sm mt-3" style={{ color: T.textDim }}>
        For now, run this round as <b>{closest}</b> — pick that format when you create the tournament.
      </p>
    </div>
  )
}

function FatalError({ msg }) {
  return (
    <div className="min-h-full flex items-center justify-center p-6" style={{ backgroundColor: PBCC_GREEN }}>
      <div className="bg-white rounded-2xl p-8 max-w-sm text-center" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
        <div className="text-5xl mb-3">⚠️</div>
        <p className="font-bold text-gray-800">{msg}</p>
      </div>
    </div>
  )
}

function Loading() {
  // Match the saved theme so a light-mode user doesn't get a dark-green flash
  // before the scorecard paints. Read straight from localStorage — this
  // renders before the tournament (and the theme provider) are ready.
  let isDark = false
  try { isDark = localStorage.getItem('scorecardTheme') === 'dark' } catch {}
  const T = isDark ? SCORE_THEMES.dark : SCORE_THEMES.light
  return (
    <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: T.pageBg, color: T.text }}>
      Loading…
    </div>
  )
}

// Pace-of-play banner. Only renders when this scorecard's group is
// late or about-to-be-late — green status renders nothing so the
// scorecard stays uncluttered. Yellow shows the live mm:ss remaining,
// red appears for the source group only (delayed = collateral group
// behind a slow upstream group, no banner per spec).
function PaceBanner({ status }) {
  if (!status) return null
  if (status.status === 'yellow') {
    return (
      <div
        className="rounded-2xl px-4 py-2.5 text-center"
        style={{
          background: 'linear-gradient(180deg, #FBBF24 0%, #F59E0B 100%)',
          color: '#123D26',
          border: '1.5px solid #123D26',
          fontWeight: 800,
        }}>
        ⏱ {formatRemaining(status.remainingMs)} remaining on this hole
      </div>
    )
  }
  if (status.status === 'red' && status.isSource) {
    return (
      <div
        className="rounded-2xl px-4 py-3 text-center"
        style={{
          background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
          color: '#FFFFFF',
          border: '1.5px solid #FFFFFF',
          fontWeight: 900,
          letterSpacing: '0.04em',
        }}>
        🔴 PACE OF PLAY — Please speed up
        {status.minutesOver > 0 ? (
          <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2, opacity: 0.85 }}>
            {status.minutesOver} min over budget · submit this hole's score to clear
          </div>
        ) : null}
      </div>
    )
  }
  return null
}
